import { useConnection } from "@solana/wallet-adapter-react";
import type { OpenOrdersResponse } from "@stepfinance/dex";
import { OpenbookPositionFetcher } from "@stepfinance/dex";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useMarkets } from "../../../contexts/market";
import { cache } from "../../../utils/cache";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { useConnectedWallet } from "../../wallet";

const DEFAULT_OPEN_ORDERS: OpenOrdersResponse = {
  marketsMap: new Map(),
  openOrdersMap: new Map(),
  unsettledBalances: [],
  closableOpenOrders: [],
  orders: [],
  totalOrdersValueUSD: null,
  totalUnsettledValueUSD: null,
  totalClosableRentSOL: 0,
};

export const useOpenbookOrdersQuery = () => {
  const { priceInUSD, marketsInitialised } = useMarkets();
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const fetcher = useMemo(
    () =>
      wallet &&
      new OpenbookPositionFetcher({
        connection,
        publicKey: wallet.publicKey,
        getTokenByMint,
        queryMint: (mintAddress: string) => cache.queryMint(connection, mintAddress),
      }),
    [connection, wallet],
  );

  return useQuery<OpenOrdersResponse>(
    ["openbookOrders", wallet?.publicKey.toBase58()],
    async () => {
      if (fetcher) {
        return await fetcher.fetchPositions(priceInUSD);
      }
      return DEFAULT_OPEN_ORDERS;
    },
    {
      retry: false,
      enabled: !!fetcher && marketsInitialised && !!priceInUSD,
      staleTime: 30000,
    },
  );
};
